import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import CenterSelectionModal from "../components/booking/CenterSelectionModal";
import CalendarView from "../components/booking/CalendarView";
import BookingForm from "../components/booking/BookingForm";
import {
  CalendarPlus,
  CalendarClock,
  Printer,
  FileText,
  Search,
  Calendar,
  QrCode,
  User,
  CheckCircle2,
  ChevronRight,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const TABS = [
  { key: "book", label: "Book Appointment", icon: CalendarPlus },
  { key: "reschedule", label: "Re-schedule Appointment", icon: CalendarClock },
  { key: "reprint", label: "Re-print Application", icon: Printer },
];

/* ── Mock result data shown after search ── */
const MOCK_RESULT = {
  name: "Rahul Sharma",
  refNo: "ICAC-2024-000123",
  dob: "1990-05-14",
  service: "Tourist Visa",
  appointmentDate: "24 Feb 2026, 10:30 AM",
  status: "Confirmed",
  photo: null, // replace with actual URL
  qr: null, // replace with actual QR URL
  documents: [
    { label: "Valid Passport (original + copy)", required: true },
    { label: "Completed Visa Application Form", required: true },
    { label: "Recent Passport-size Photograph (2 copies)", required: true },
    { label: "Proof of Residence in Korea", required: true },
    { label: "Flight Itinerary / Invitation Letter", required: true },
    { label: "Bank Statement (last 3 months)", required: false },
    { label: "Hotel Booking / Accommodation Proof", required: false },
  ],
};

/* ── Inline search form ── */
const InlineSearchForm = ({ icon: Icon, title, description, buttonLabel }) => {
  const [refNo, setRefNo] = useState("");
  const [dob, setDob] = useState("");
  const [result, setResult] = useState(null);
  const [searched, setSearched] = useState(false);

  const inputStyle = {
    width: "100%",
    height: 44,
    padding: "0 12px 0 34px",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13.5,
    color: "#1a2e3d",
    background: "#f4f7f9",
    border: "1px solid rgba(15,31,46,0.1)",
    borderRadius: 7,
    outline: "none",
    boxSizing: "border-box",
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSearched(true);
    setResult(MOCK_RESULT); // wire to real API here
  };

  const handleReset = () => {
    setResult(null);
    setSearched(false);
    setRefNo("");
    setDob("");
  };

  return (
    <div style={{ maxWidth: 860 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 14,
          marginBottom: 28,
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            flexShrink: 0,
            borderRadius: 10,
            background: "rgba(201,168,76,0.08)",
            border: "1px solid rgba(201,168,76,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#c9a84c",
            marginTop: 2,
          }}
        >
          <Icon size={20} strokeWidth={1.6} />
        </div>
        <div>
          <p
            style={{
              fontFamily: "'Lora', serif",
              fontSize: 20,
              fontWeight: 500,
              color: "#1a2e3d",
              marginBottom: 4,
            }}
          >
            {title}
          </p>
          <p style={{ fontSize: 13.5, color: "#4a6475", lineHeight: 1.65 }}>
            {description}
          </p>
        </div>
      </div>

      {/* Search card */}
      <div
        style={{
          background: "#fff",
          border: "1px solid rgba(15,31,46,0.1)",
          borderRadius: 10,
          padding: "20px 20px",
          marginBottom: 24,
        }}
      >
        <form
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          {/* Ref No */}
          <div style={{ flex: "1 1 200px" }}>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#546e7a",
                marginBottom: 7,
              }}
            >
              Application Ref. No <span style={{ color: "#c9a84c" }}>*</span>
            </label>
            <div style={{ position: "relative" }}>
              <FileText
                size={14}
                strokeWidth={1.7}
                style={{
                  position: "absolute",
                  left: 11,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#a0b8c4",
                  pointerEvents: "none",
                }}
              />
              <input
                type="text"
                placeholder="e.g. ICAC-2024-000123"
                value={refNo}
                onChange={(e) => setRefNo(e.target.value)}
                required
                style={inputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = "#c9a84c";
                  e.target.style.boxShadow = "0 0 0 3px rgba(201,168,76,0.1)";
                  e.target.style.background = "#fff";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "rgba(15,31,46,0.1)";
                  e.target.style.boxShadow = "none";
                  e.target.style.background = "#f4f7f9";
                }}
              />
            </div>
          </div>

          {/* DOB */}
          <div style={{ flex: "1 1 180px" }}>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#546e7a",
                marginBottom: 7,
              }}
            >
              Date of Birth <span style={{ color: "#c9a84c" }}>*</span>
            </label>
            <div style={{ position: "relative" }}>
              <Calendar
                size={14}
                strokeWidth={1.7}
                style={{
                  position: "absolute",
                  left: 11,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#a0b8c4",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                required
                style={{
                  ...inputStyle,
                  paddingLeft: 34,
                  color: dob ? "#1a2e3d" : "#a0b8c4",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#c9a84c";
                  e.target.style.boxShadow = "0 0 0 3px rgba(201,168,76,0.1)";
                  e.target.style.background = "#fff";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "rgba(15,31,46,0.1)";
                  e.target.style.boxShadow = "none";
                  e.target.style.background = "#f4f7f9";
                }}
              />
            </div>
          </div>

          {/* Button */}
          <button
            type="submit"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              height: 44,
              padding: "0 22px",
              background: "#0f1f2e",
              color: "#d9e8f0",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              border: "none",
              borderRadius: 7,
              cursor: "pointer",
              flexShrink: 0,
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = "#162534")}
            onMouseOut={(e) => (e.currentTarget.style.background = "#0f1f2e")}
          >
            <Search size={13} strokeWidth={2.2} />
            {buttonLabel}
          </button>
        </form>
      </div>

      {/* ── Result panel ── */}
      {result && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Top: Photo + QR + Info */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto auto 1fr",
              gap: 20,
              background: "#fff",
              border: "1px solid rgba(15,31,46,0.1)",
              borderRadius: 10,
              padding: 24,
              marginBottom: 16,
              alignItems: "start",
              flexWrap: "wrap",
            }}
          >
            {/* Applicant Photo */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div
                style={{
                  width: 88,
                  height: 104,
                  borderRadius: 8,
                  background: "#f4f7f9",
                  border: "1px solid rgba(15,31,46,0.1)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#a0b8c4",
                  overflow: "hidden",
                }}
              >
                {result.photo ? (
                  <img
                    src={result.photo}
                    alt="applicant"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <>
                    <User size={28} strokeWidth={1.2} />
                    <span
                      style={{ fontSize: 10, marginTop: 4, color: "#a0b8c4" }}
                    >
                      Photo
                    </span>
                  </>
                )}
              </div>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                  color: "#546e7a",
                }}
              >
                Applicant
              </span>
            </div>

            {/* QR Code */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div
                style={{
                  width: 104,
                  height: 104,
                  borderRadius: 8,
                  background: "#f4f7f9",
                  border: "1px solid rgba(15,31,46,0.1)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#a0b8c4",
                  padding: 8,
                }}
              >
                {result.qr ? (
                  <img
                    src={result.qr}
                    alt="QR Code"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
                    {/* QR placeholder SVG */}
                    <rect
                      x="4"
                      y="4"
                      width="28"
                      height="28"
                      rx="2"
                      stroke="#c9a84c"
                      strokeWidth="2.5"
                      fill="none"
                    />
                    <rect
                      x="10"
                      y="10"
                      width="16"
                      height="16"
                      rx="1"
                      fill="#c9a84c"
                      opacity="0.5"
                    />
                    <rect
                      x="40"
                      y="4"
                      width="28"
                      height="28"
                      rx="2"
                      stroke="#c9a84c"
                      strokeWidth="2.5"
                      fill="none"
                    />
                    <rect
                      x="46"
                      y="10"
                      width="16"
                      height="16"
                      rx="1"
                      fill="#c9a84c"
                      opacity="0.5"
                    />
                    <rect
                      x="4"
                      y="40"
                      width="28"
                      height="28"
                      rx="2"
                      stroke="#c9a84c"
                      strokeWidth="2.5"
                      fill="none"
                    />
                    <rect
                      x="10"
                      y="46"
                      width="16"
                      height="16"
                      rx="1"
                      fill="#c9a84c"
                      opacity="0.5"
                    />
                    <rect
                      x="40"
                      y="40"
                      width="6"
                      height="6"
                      rx="1"
                      fill="#1a2e3d"
                    />
                    <rect
                      x="50"
                      y="40"
                      width="6"
                      height="6"
                      rx="1"
                      fill="#1a2e3d"
                    />
                    <rect
                      x="60"
                      y="40"
                      width="8"
                      height="6"
                      rx="1"
                      fill="#1a2e3d"
                    />
                    <rect
                      x="40"
                      y="50"
                      width="14"
                      height="6"
                      rx="1"
                      fill="#1a2e3d"
                    />
                    <rect
                      x="58"
                      y="50"
                      width="10"
                      height="6"
                      rx="1"
                      fill="#1a2e3d"
                    />
                    <rect
                      x="40"
                      y="60"
                      width="6"
                      height="8"
                      rx="1"
                      fill="#1a2e3d"
                    />
                    <rect
                      x="52"
                      y="60"
                      width="16"
                      height="8"
                      rx="1"
                      fill="#1a2e3d"
                    />
                  </svg>
                )}
              </div>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                  color: "#546e7a",
                }}
              >
                Scan QR
              </span>
            </div>

            {/* Applicant details */}
            <div style={{ paddingLeft: 8 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 14,
                }}
              >
                <p
                  style={{
                    fontFamily: "'Lora', serif",
                    fontSize: 18,
                    fontWeight: 500,
                    color: "#1a2e3d",
                  }}
                >
                  {result.name}
                </p>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "#2e7d52",
                    background: "rgba(46,125,82,0.08)",
                    border: "1px solid rgba(46,125,82,0.2)",
                    borderRadius: 4,
                    padding: "2px 8px",
                  }}
                >
                  {result.status}
                </span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px 24px",
                }}
              >
                {[
                  { label: "Reference No.", value: result.refNo },
                  { label: "Service", value: result.service },
                  { label: "Date of Birth", value: result.dob },
                  { label: "Appointment", value: result.appointmentDate },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <p
                      style={{
                        fontSize: 10.5,
                        fontWeight: 600,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "#546e7a",
                        marginBottom: 2,
                      }}
                    >
                      {label}
                    </p>
                    <p
                      style={{
                        fontSize: 13,
                        color: "#1a2e3d",
                        fontWeight: 500,
                      }}
                    >
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Required Documents */}
          <div
            style={{
              background: "#fff",
              border: "1px solid rgba(15,31,46,0.1)",
              borderRadius: 10,
              padding: 24,
            }}
          >
            <p
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#c9a84c",
                marginBottom: 16,
                paddingBottom: 10,
                borderBottom: "1px solid rgba(201,168,76,0.2)",
              }}
            >
              Required Documents
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px 24px",
              }}
            >
              {result.documents.map((doc, i) => (
                <div
                  key={i}
                  style={{ display: "flex", alignItems: "flex-start", gap: 10 }}
                >
                  <CheckCircle2
                    size={15}
                    strokeWidth={1.8}
                    style={{
                      flexShrink: 0,
                      marginTop: 1,
                      color: doc.required ? "#c9a84c" : "#a0b8c4",
                    }}
                  />
                  <div>
                    <p
                      style={{
                        fontSize: 13,
                        color: "#1a2e3d",
                        lineHeight: 1.5,
                      }}
                    >
                      {doc.label}
                    </p>
                    <p
                      style={{
                        fontSize: 11,
                        color: doc.required ? "#c9a84c" : "#a0b8c4",
                        fontWeight: 600,
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                      }}
                    >
                      {doc.required ? "Mandatory" : "If applicable"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Reset */}
          <button
            onClick={handleReset}
            style={{
              marginTop: 16,
              background: "none",
              border: "none",
              fontSize: 12,
              color: "#8aadbe",
              cursor: "pointer",
              textDecoration: "underline",
              padding: 0,
            }}
          >
            ← Search again
          </button>
        </motion.div>
      )}

      {!result && (
        <p style={{ fontSize: 12, color: "#8aadbe", lineHeight: 1.6 }}>
          Details must match exactly as submitted during your original
          application.
        </p>
      )}
    </div>
  );
};

/* ══════════════════════════════════════ */
const AppointmentManagement = () => {
  const location = useLocation();

  const [activeTab, setActiveTab] = useState("book");
  const [step, setStep] = useState(() =>
    location.state?.center && location.state?.slot ? 3 : 0,
  );
  const [selectedCenter, setSelectedCenter] = useState(
    () => location.state?.center || null,
  );
  const [selectedSlot, setSelectedSlot] = useState(
    () => location.state?.slot || null,
  );
  const [modalOpen, setModalOpen] = useState(false);

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === "book") {
      setStep(0);
      setSelectedCenter(null);
      setSelectedSlot(null);
      setModalOpen(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,500;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        .am-page { font-family: 'DM Sans', sans-serif; background: #f4f7f9; min-height: 100vh; color: #1a2e3d; }
        .am-hero { background: #0f1f2e; padding: 48px 32px 0; border-bottom: 2px solid #c9a84c; }
        .am-hero-inner { max-width: 960px; margin: 0 auto; }
        .am-eyebrow { font-size: 10.5px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: #c9a84c; margin-bottom: 10px; }
        .am-hero h1 { font-family: 'Lora', serif; font-size: 32px; font-weight: 500; color: #d9e8f0; margin-bottom: 6px; line-height: 1.25; }
        .am-hero-sub { font-size: 14px; font-weight: 300; color: #7a98a8; margin-bottom: 32px; }
        .am-tabs { display: flex; gap: 4px; align-items: flex-end; }
        .am-tab { display: flex; align-items: center; gap: 8px; padding: 12px 22px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 500; color: #546e7a; background: transparent; border: none; border-radius: 8px 8px 0 0; cursor: pointer; white-space: nowrap; transition: background 0.18s, color 0.18s; }
        .am-tab:hover { background: rgba(255,255,255,0.06); color: #aac8d6; }
        .am-tab.active { background: #f4f7f9; color: #0f1f2e; font-weight: 600; }
        .am-tab-icon { color: #546e7a; transition: color 0.18s; flex-shrink: 0; }
        .am-tab:hover .am-tab-icon { color: #aac8d6; }
        .am-tab.active .am-tab-icon { color: #c9a84c; }
        .am-content { max-width: 960px; margin: 0 auto; padding: 40px 32px 24px; }
        @media (max-width: 700px) {
          .am-hero { padding: 36px 20px 0; }
          .am-hero h1 { font-size: 26px; }
          .am-content { padding: 28px 16px 52px; }
          .am-tab { padding: 10px 14px; font-size: 12px; }
          .am-tab span { display: none; }
        }
      `}</style>

      <Header />
      <div className="am-page">
        <div className="am-hero">
          <div className="am-hero-inner">
            <h1>Appointment Management</h1>
            <p className="am-hero-sub">
              Book, reschedule, or reprint your ICAC appointment.
            </p>
            <div className="am-tabs">
              {TABS.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  className={`am-tab ${activeTab === key ? "active" : ""}`}
                  onClick={() => handleTabChange(key)}
                >
                  <Icon size={15} strokeWidth={1.8} className="am-tab-icon" />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="am-content">
          <AnimatePresence mode="wait">
            {activeTab === "book" && (
              <motion.div
                key="book"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                <AnimatePresence mode="wait">
                  {step === 0 && (
                    <motion.div
                      key="book-start"
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -12 }}
                      style={{ textAlign: "center", padding: "64px 0" }}
                    >
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 12,
                          background: "rgba(201,168,76,0.1)",
                          border: "1px solid rgba(201,168,76,0.25)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 20px",
                          color: "#c9a84c",
                        }}
                      >
                        <CalendarPlus size={24} strokeWidth={1.6} />
                      </div>
                      <p
                        style={{
                          fontFamily: "'Lora', serif",
                          fontSize: 22,
                          fontWeight: 500,
                          color: "#1a2e3d",
                          marginBottom: 10,
                        }}
                      >
                        Book a New Appointment
                      </p>
                      <p
                        style={{
                          fontSize: 14,
                          color: "#4a6475",
                          lineHeight: 1.75,
                          maxWidth: 400,
                          margin: "0 auto 28px",
                        }}
                      >
                        Schedule your appointment in just a
                        few simple steps.
                      </p>
                      <button
                        onClick={() => {
                          setModalOpen(true);
                          setStep(1);
                        }}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "12px 28px",
                          background: "#0f1f2e",
                          color: "#d9e8f0",
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: 13,
                          fontWeight: 600,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          border: "none",
                          borderRadius: 8,
                          cursor: "pointer",
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.background = "#162534")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.background = "#0f1f2e")
                        }
                      >
                        <CalendarPlus size={15} strokeWidth={2} /> Start Booking
                      </button>
                    </motion.div>
                  )}
                  {step === 2 && selectedCenter && (
                    <motion.div
                      key="calendar"
                      initial={{ opacity: 0, x: 40 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -40 }}
                    >
                      <CalendarView
                        center={selectedCenter}
                        onSelectSlot={(slot) => {
                          setSelectedSlot(slot);
                          setStep(3);
                        }}
                        onBack={() => {
                          setModalOpen(true);
                          setStep(1);
                        }}
                      />
                    </motion.div>
                  )}
                  {step === 3 && selectedCenter && selectedSlot && (
                    <motion.div
                      key="form"
                      initial={{ opacity: 0, x: 40 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -40 }}
                    >
                      <BookingForm
                        center={selectedCenter}
                        slot={selectedSlot}
                        onBack={() => setStep(2)}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {activeTab === "reschedule" && (
              <motion.div
                key="reschedule"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                <InlineSearchForm
                  icon={CalendarClock}
                  title="Re-schedule Appointment"
                  description="Locate your booking using your reference number and date of birth, then choose a new available slot."
                  buttonLabel="Find Appointment"
                />
              </motion.div>
            )}

            {activeTab === "reprint" && (
              <motion.div
                key="reprint"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                <InlineSearchForm
                  icon={Printer}
                  title="Re-print Application"
                  description="Retrieve your submitted application form to download or print a fresh copy for your records."
                  buttonLabel="Retrieve & Print"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <CenterSelectionModal
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            if (step === 2) setStep(0);
          }}
          onSelectCenter={(center) => {
            setSelectedCenter(center);
            setStep(2);
          }}
        />
      </div>
      <Footer />
    </>
  );
};

export default AppointmentManagement;
